import type { CartItem, CartOptions } from '../services/cartService';
import type { MenuItem, MenuItemDetail } from '../services/restaurantService';
import { MenuOrderSelections, noteKey, normalizeNote } from './menuOptions';

/**
 * Cart line identity (design D5/D10), same as LineOptions.lineKey on the server:
 * menuId | sizeTypeId | sorted option ids | normalised note.
 */

type DefaultVariantItem = Pick<MenuItem | MenuItemDetail, 'defaultSizeTypeId'> | null | undefined;

/** Sorted, de-duplicated, comma-joined option ids; "" for none. */
export function modifierSignature(optionIds?: Array<number | null | undefined> | null): string {
  const ids: number[] = [];
  (optionIds || []).forEach((id) => {
    const n = Number(id);
    if (Number.isInteger(n) && n > 0 && ids.indexOf(n) < 0) {
      ids.push(n);
    }
  });
  ids.sort((a, b) => a - b);
  return ids.join(',');
}

export function parseModifierSignature(signature?: string | null): number[] {
  if (!signature) return [];
  return modifierSignature(signature.split(',').map((p) => Number(p.trim())))
    .split(',')
    .filter(Boolean)
    .map(Number);
}

export function lineKey(
  menuId: number,
  sizeTypeId: number | null | undefined,
  signature: string,
  note: string | null | undefined
): string {
  return `${menuId}|${sizeTypeId ?? ''}|${signature}|${noteKey(note)}`;
}

/**
 * Option ids of the STORED line, as the server returned them (`modifierSignature`, else
 * `modifierOptionIds`). Never derived from `modifiers[]`: that list is what is billed now and
 * drops options that went out of stock, so it would no longer identify the stored line.
 */
function storedOptionIds(line: CartItem): number[] {
  if (typeof line.modifierSignature === 'string') {
    return parseModifierSignature(line.modifierSignature);
  }
  return parseModifierSignature(modifierSignature(line.modifierOptionIds));
}

function lineSignature(line: CartItem): string {
  return storedOptionIds(line).join(',');
}

export function cartLineKey(line: CartItem): string {
  return lineKey(line.menuId, line.sizeTypeId, lineSignature(line), line.itemNote);
}

/** Unique React key for a cart row (guest lines have no cartId: the full line key is unique). */
export function cartLineReactKey(line: CartItem): string {
  return `${typeof line.cartId === 'number' ? line.cartId : ''}#${cartLineKey(line)}`;
}

/** Variant to send when the selection has none (single-variant / legacy items). */
export function resolveSizeTypeId(
  item: DefaultVariantItem,
  selections: MenuOrderSelections
): number | undefined {
  return selections.sizeTypeId ?? item?.defaultSizeTypeId ?? undefined;
}

export function selectionLineKey(
  menuId: number,
  selections: MenuOrderSelections,
  item?: DefaultVariantItem
): string {
  return lineKey(
    menuId,
    resolveSizeTypeId(item, selections),
    modifierSignature(selections.modifierOptionIds),
    selections.itemNote
  );
}

/** True when the cart row is the line for this menu item + selection. */
export function cartLineMatches(
  line: CartItem,
  menuId: number,
  selections: MenuOrderSelections,
  item?: DefaultVariantItem
): boolean {
  if (line.menuId !== menuId) {
    return false;
  }
  if (resolveSizeTypeId(item, selections) == null) {
    // Legacy item without variants: the server picks the only variant.
    return (
      lineSignature(line) === modifierSignature(selections.modifierOptionIds) &&
      noteKey(line.itemNote) === noteKey(selections.itemNote)
    );
  }
  return cartLineKey(line) === selectionLineKey(menuId, selections, item);
}

export function findCartLine(
  cartItems: CartItem[],
  menuId: number,
  selections: MenuOrderSelections,
  item?: DefaultVariantItem
): CartItem | undefined {
  return cartItems.find((line) => cartLineMatches(line, menuId, selections, item));
}

export function cartLinesForMenu(cartItems: CartItem[], menuId: number): CartItem[] {
  return cartItems.filter((line) => line.menuId === menuId);
}

/** Most recently added line (highest cartId; array order for guest carts without ids). */
export function lastCartLine(lines: CartItem[]): CartItem | undefined {
  if (lines.length === 0) return undefined;
  if (lines.every((l) => typeof l.cartId === 'number')) {
    return lines.reduce((a, b) => (b.cartId > a.cartId ? b : a));
  }
  return lines[lines.length - 1];
}

/** Selection to pre-fill the picker with when editing a line (the stored identity). */
export function selectionsFromCartLine(line: CartItem): MenuOrderSelections {
  return {
    sizeTypeId: line.sizeTypeId ?? undefined,
    modifierOptionIds: storedOptionIds(line),
    itemNote: line.itemNote || '',
  };
}

/** Request options for a selection (what POST /cart/add needs to identify the line). */
export function cartOptionsFor(selections: MenuOrderSelections, item?: DefaultVariantItem): CartOptions {
  const note = normalizeNote(selections.itemNote);
  return {
    sizeTypeId: resolveSizeTypeId(item, selections),
    modifierOptionIds: parseModifierSignature(modifierSignature(selections.modifierOptionIds)),
    ...(note ? { itemNote: note } : {}),
  };
}

/**
 * Request options that identify an EXISTING line (change its quantity, or 0 to remove it): the
 * stored variant, option ids and note sent back exactly as the server returned them.
 */
export function cartOptionsForLine(line: CartItem): CartOptions {
  const note = line.itemNote ?? '';
  return {
    sizeTypeId: line.sizeTypeId ?? undefined,
    modifierOptionIds: storedOptionIds(line),
    ...(note ? { itemNote: note } : {}),
  };
}

/** One-line description of a cart line for prompts ("Medium · + Extra cheese"). */
export function cartLineSummary(line: CartItem): string {
  const parts: string[] = [];
  const summary = line.optionsSummary?.trim();
  if (summary) {
    parts.push(summary);
  } else if (line.variantName) {
    parts.push(line.variantName);
  }
  const note = normalizeNote(line.itemNote);
  if (note) {
    parts.push(`“${note}”`);
  }
  return parts.join(' · ');
}
